import React, { useState, useRef, useEffect } from 'react';
import { Trash2, CornerUpLeft, Copy, Check } from 'lucide-react';

const REACTIONS = ['👍', '❤️', '😂', '😮', '😢'];

/**
 * Mobile bottom-drawer for long-press message actions.
 *
 * Layout:
 *   ── Emoji pill (fixed, above the long-pressed message) ──
 *   ░░░░░░░░░░░ backdrop ░░░░░░░░░░░
 *   ── Action panel (bottom sheet) ──
 *
 * Props:
 *   msg              - message object (canDelete, reactions, text, message_id)
 *   userId           - current user id (for reaction highlight)
 *   touchY           - clientY of the long-press touch (positions the emoji pill)
 *   senderDisplayName- display name for reply snapshot
 *   onReact          - (msgId, emoji) => void
 *   onDelete         - (msgId) => void
 *   onClose          - () => void
 *   onReply          - optional (snapshot) => void
 */
const ChatActionDrawer = ({
  msg,
  userId,
  touchY,
  senderDisplayName,
  onReact,
  onDelete,
  onClose,
  onReply,
}) => {
  const mountedAt = useRef(Date.now());
  const [dragY,       setDragY]       = useState(0);
  const dragStartY                    = useRef(0);
  const [deleteArmed, setDeleteArmed] = useState(false);
  const deleteTimerRef                = useRef(null);
  const [copied,      setCopied]      = useState(false);

  useEffect(() => () => clearTimeout(deleteTimerRef.current), []);

  if (!msg) return null;

  /* ── Debounced close (prevents synthetic click from closing on open) ── */
  const handleClose = () => {
    if (Date.now() - mountedAt.current < 400) return;
    onClose();
  };

  /* ── Swipe-down to dismiss ── */
  const onDragStart = (e) => { dragStartY.current = e.touches[0].clientY; };
  const onDragMove  = (e) => {
    const delta = e.touches[0].clientY - dragStartY.current;
    if (delta > 0) setDragY(delta);
  };
  const onDragEnd = () => {
    if (dragY > 80) onClose();
    else setDragY(0);
  };

  /* ── Copy ── */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(msg.text || '');
      setCopied(true);
      setTimeout(() => { setCopied(false); onClose(); }, 900);
    } catch {
      onClose();
    }
  };

  /* ── Two-tap delete guard ── */
  const handleDeleteTap = () => {
    if (!deleteArmed) {
      setDeleteArmed(true);
      deleteTimerRef.current = setTimeout(() => setDeleteArmed(false), 2000);
    } else {
      clearTimeout(deleteTimerRef.current);
      onDelete(msg.message_id);
      onClose();
    }
  };

  /* ── Emoji pill position: just above the touch point, clamped to viewport ── */
  // Pill is ~56 px tall; leave a 12 px gap above touch point.
  const PILL_H = 56;
  const PILL_GAP = 12;
  const pillTop = touchY != null
    ? Math.max(8, touchY - PILL_H - PILL_GAP)
    : undefined;

  /* ── Icon action definitions ── */
  const actions = [
    onReply && {
      id:      'reply',
      icon:    <CornerUpLeft className="w-6 h-6" />,
      label:   'Reply',
      testId:  'drawer-reply-btn',
      color:   'text-slate-700 dark:text-slate-200',
      bg:      'bg-slate-100 dark:bg-slate-700',
      onClick: () => {
        onReply({
          message_id:  msg.message_id,
          sender_name: msg.sender_name || senderDisplayName,
          text:        (msg.text || '').slice(0, 100),
        });
        onClose();
      },
    },
    {
      id:      'copy',
      icon:    copied ? <Check className="w-6 h-6" /> : <Copy className="w-6 h-6" />,
      label:   copied ? 'Copied!' : 'Copy',
      testId:  'drawer-copy-btn',
      color:   copied ? 'text-green-600 dark:text-green-400' : 'text-slate-700 dark:text-slate-200',
      bg:      copied ? 'bg-green-100 dark:bg-green-900/30' : 'bg-slate-100 dark:bg-slate-700',
      onClick: handleCopy,
    },
    msg.canDelete && {
      id:      'delete',
      icon:    <Trash2 className="w-6 h-6" />,
      label:   deleteArmed ? 'Confirm?' : 'Delete',
      testId:  'drawer-delete-btn',
      color:   deleteArmed ? 'text-white'   : 'text-red-600 dark:text-red-400',
      bg:      deleteArmed ? 'bg-red-500'   : 'bg-red-50 dark:bg-red-900/30',
      onClick: handleDeleteTap,
    },
  ].filter(Boolean);

  return (
    <div className="fixed inset-0 z-50" data-testid="msg-action-drawer">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={handleClose}
        onTouchEnd={(e) => { e.preventDefault(); handleClose(); }}
      />

      {/* ── Floating emoji pill — above the long-pressed message ── */}
      <div
        style={pillTop != null ? {
          position: 'fixed',
          top: `${pillTop}px`,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 60,
        } : {
          /* Fallback: old bottom layout */
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: '12px',
        }}
        className="bg-white dark:bg-slate-800 rounded-full shadow-2xl border border-slate-100 dark:border-slate-700 px-3 py-2 flex items-center gap-1 whitespace-nowrap"
        onClick={(e) => e.stopPropagation()}
      >
        {REACTIONS.map(emoji => {
          const reacted = ((msg.reactions || {})[emoji] || []).includes(userId);
          return (
            <button
              key={emoji}
              onClick={() => { onReact(msg.message_id, emoji); onClose(); }}
              data-testid={`drawer-react-${emoji}`}
              className={`text-xl leading-none p-1.5 hover:scale-110 active:scale-95 transition-transform rounded-full ${
                reacted ? 'bg-green-500 dark:bg-green-600 ring-2 ring-green-400 dark:ring-green-500' : ''
              }`}
            >
              {emoji}
            </button>
          );
        })}
      </div>

      {/* ── Compact action panel ── */}
      <div
        className="absolute bottom-0 w-full bg-white dark:bg-slate-800 rounded-t-3xl shadow-2xl"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 16px)',
          transform:     `translateY(${dragY}px)`,
          transition:    dragY === 0 ? 'transform 0.2s ease-out' : 'none',
        }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onDragStart}
        onTouchMove={onDragMove}
        onTouchEnd={onDragEnd}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-slate-300 dark:bg-slate-600 rounded-full" />
        </div>

        {/* Icon row */}
        <div className="flex justify-around items-start px-8 py-4">
          {actions.map(action => (
            <button
              key={action.id}
              onClick={action.onClick}
              data-testid={action.testId}
              className={`flex flex-col items-center gap-2 ${action.color}`}
            >
              <div className={`w-12 h-12 rounded-full ${action.bg} flex items-center justify-center transition-colors`}>
                {action.icon}
              </div>
              <span className="text-xs font-medium">{action.label}</span>
            </button>
          ))}
        </div>

        {/* Cancel */}
        <div className="px-4 pb-3">
          <button
            onClick={onClose}
            data-testid="drawer-cancel-btn"
            className="w-full py-3 text-sm font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 rounded-xl"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatActionDrawer;
